import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatDate, formatViews } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'feature' | 'row';
  index?: number;
}

export default function ArticleCard({ article, variant = 'default', index }: ArticleCardProps) {
  const href = `/article/${article.slug}`;

  if (variant === 'feature') {
    return (
      <article className="group relative rounded-lg overflow-hidden bg-foreground-950 h-[420px] md:h-[520px]">
        <img
          src={article.cover}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-85 group-hover:opacity-95 transition-opacity duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-foreground-950/10" />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-9">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
              {article.category}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 text-xs backdrop-blur">
              <i className="ri-fire-line mr-1" />深度线报
            </span>
          </div>
          <h3 className="mt-4 font-heading text-xl md:text-3xl font-bold text-background-50 leading-snug">
            <Link to={href} className="hover:text-accent-300 transition-colors">
              {article.title}
            </Link>
          </h3>
          <p className="mt-3 text-sm text-background-200 leading-relaxed line-clamp-2 max-w-2xl">{article.excerpt}</p>
          <div className="mt-5 flex items-center gap-4 text-xs text-background-300">
            <span>{article.author}</span>
            <span>{formatDate(article.date)}</span>
            <span>阅读 {formatViews(article.views)}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'row') {
    return (
      <article className="group flex gap-4 py-5 border-b border-background-200/70 last:border-0">
        {typeof index === 'number' && (
          <span className="shrink-0 w-7 h-7 rounded-md bg-background-100 text-primary-700 font-heading font-bold text-sm flex items-center justify-center">
            {index + 1}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug">
            <Link to={href} className="hover:text-primary-600 transition-colors line-clamp-2">
              {article.title}
            </Link>
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
            <span className="text-secondary-700">{article.category}</span>
            <span>{formatDate(article.date)}</span>
            <span>阅读 {formatViews(article.views)}</span>
          </div>
        </div>
        <Link to={href} className="shrink-0 w-20 h-16 md:w-24 md:h-16 rounded-md overflow-hidden">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors">
      <Link to={href} className="block relative w-full h-48 overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-50/92 text-primary-700 text-xs font-medium backdrop-blur">
          {article.category}
        </span>
      </Link>
      <div className="flex-1 flex flex-col p-5">
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
          <Link to={href} className="hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </Link>
        </h3>
        <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">{article.excerpt}</p>
        <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between text-xs text-foreground-500">
          <span>{article.author} · {article.readTime} 分钟</span>
          <span><i className="ri-eye-line mr-1" />{formatViews(article.views)}</span>
        </div>
      </div>
    </article>
  );
}